import { useRef, useState, type ReactNode } from 'react';
import { Badge } from './Badge.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import './Rail.css';

export interface RailItem {
  id: string;
  /** Full descriptive label — becomes the accessible name and the tooltip
   * text. Never a bare icon with no name (a11y.md §5). */
  label: string;
  icon: ReactNode;
  /** A live count. Rendered as visible text alongside the icon, and folded
   * into the item's accessible name ("Findings, 4,120 items") — a11y.md §5
   * bans a bare numeric badge with no accessible context. */
  badgeCount?: number;
  /**
   * What the count MEANS, as a function of it — e.g. `(n) => \`${n} to review\``.
   * Supplied per item because these counts are not interchangeable: 08 §2 gives
   * each nav badge its own source (sources needing attention, high-impact
   * findings, undecided pairs, actions awaiting a human). Labelling them all
   * "need attention" is how a badge stops meaning anything and starts being
   * ignored. Falls back to a neutral "N items" when omitted.
   */
  badgeLabel?: (count: number) => string;
  href?: string;
  onSelect?: () => void;
  /**
   * Sub-destinations. In the expanded `Sidebar` these render as inline sub-nav;
   * in the collapsed rail they surface through the **hover/focus flyout** — the
   * prototype's `flyNav` (census §1), which is what keeps the child routes
   * reachable when the labels are hidden.
   */
  children?: { id: string; label: string; href?: string; onSelect?: () => void }[];
}

export interface RailProps {
  items: RailItem[];
  activeId?: string;
  /**
   * Expands the rail back to the labelled `Sidebar`. Rendered as the hamburger in
   * the rail's own 60px header row — **not** in the topbar, which is where the
   * build first put it.
   *
   * The prototype's nav carries a `height: 60px` header at every width, and the
   * hamburger lives in it whether the nav is a rail or expanded; the control does
   * not move between the two states, it stays in the same place and the panel
   * beside it changes width. Putting it in the topbar when collapsed made it look
   * like a topbar action and left the rail with no header at all.
   */
  onExpand?: () => void;
  /**
   * The block under the hamburger — the org switcher's rail form. The prototype
   * keeps org identity visible when collapsed (`navIsRail`) rather than dropping
   * it: a product that writes to a customer's CRM should never hide which
   * customer you are pointed at, and the collapsed rail is exactly where that is
   * easiest to lose track of.
   */
  header?: ReactNode;
  /**
   * NOTE: the pinned help entry that used to live here is gone (2026-07-28, owner
   * decision). The prototype has exactly ONE help affordance — the 36px circle in
   * the topbar — and `AppShell` owns it. A second entry point in the nav was mine,
   * not the design's, and it put two controls in the chrome that opened the same
   * panel. `AppShell` still *requires* `onHelpClick`, so the guarantee that help is
   * reachable from every screen is unchanged; only its duplicate is removed.
   */
}

/**
 * The primary icon-only navigation rail (docs/design-system/components.md
 * Tier 2). Owns the one fixed affordance the whole app shell depends on:
 * the contextual-help entry point, which this component guarantees is
 * always rendered — a caller cannot construct a Rail without one.
 */
export function Rail({ items, activeId, onExpand, header }: RailProps) {
  /**
   * The flyout — the prototype's `flyNav`/`flyoutKeep`/`flyoutClose` trio: opens
   * on hover or keyboard focus of a rail item that has children, and closes on a
   * short delay so the pointer can cross the gap between item and panel without
   * the panel vanishing under it (the prototype clears the same timer in
   * `flyoutKeep`). Focus-driven opening is what keeps the child routes reachable
   * without a mouse: Tab onto the item opens it, further Tabs walk its rows,
   * Escape dismisses.
   */
  const [flyId, setFlyId] = useState<string | null>(null);
  const flyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keepFly = (id: string) => {
    if (flyTimer.current) clearTimeout(flyTimer.current);
    setFlyId(id);
  };
  const releaseFly = () => {
    if (flyTimer.current) clearTimeout(flyTimer.current);
    flyTimer.current = setTimeout(() => setFlyId(null), 180);
  };

  return (
    <nav className="dpr-rail" aria-label="Primary">
      {/* Same 60px as the topbar beside it, so the two hairlines line up into one
          bar — the identical guarantee `Sidebar`'s header makes when expanded. */}
      {onExpand ? (
        <div className="dpr-rail__header">
          <IconButton
            size="md"
            variant="ghost"
            label="Expand navigation"
            icon={<Icon name="menu" />}
            aria-expanded={false}
            onClick={onExpand}
          />
        </div>
      ) : null}
      {header ? <div className="dpr-rail__org">{header}</div> : null}
      <ul className="dpr-rail__list">
        {items.map((item) => {
          const isActive = item.id === activeId;
          // The badge is aria-hidden and shows a bare number, so this accessible
          // name is the *only* place the count carries meaning. Honour the
          // caller's wording: "Sources, 1 needs attention" is actionable where
          // "Sources, 1 items" is both ungrammatical and semantically empty.
          const badgeText =
            item.badgeCount != null
              ? (item.badgeLabel?.(item.badgeCount) ??
                `${item.badgeCount} item${item.badgeCount === 1 ? '' : 's'}`)
              : null;
          const accessibleName = badgeText ? `${item.label}, ${badgeText}` : item.label;
          const Element = item.href ? 'a' : 'button';
          const hasFly = (item.children?.length ?? 0) > 0;
          return (
            <li
              key={item.id}
              className="dpr-rail__li"
              onMouseEnter={hasFly ? () => keepFly(item.id) : undefined}
              onMouseLeave={hasFly ? releaseFly : undefined}
              onFocus={hasFly ? () => keepFly(item.id) : undefined}
              onBlur={hasFly ? releaseFly : undefined}
              onKeyDown={
                hasFly
                  ? (e) => {
                      if (e.key === 'Escape') setFlyId(null);
                    }
                  : undefined
              }
            >
              <Element
                className={['dpr-rail__item', isActive ? 'dpr-rail__item--active' : null]
                  .filter(Boolean)
                  .join(' ')}
                href={item.href}
                type={item.href ? undefined : 'button'}
                aria-current={isActive ? 'page' : undefined}
                aria-label={accessibleName}
                title={item.label}
                onClick={item.onSelect}
              >
                <span className="dpr-rail__icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.badgeCount != null ? (
                  <span className="dpr-rail__badge" aria-hidden="true">
                    <Badge variant="accent">{item.badgeCount > 99 ? '99+' : item.badgeCount}</Badge>
                  </span>
                ) : null}
              </Element>

              {hasFly && flyId === item.id ? (
                <div className="dpr-rail__flyout" role="group" aria-label={`${item.label} sections`}>
                  {/* The header row repeats the section label and navigates to
                      its landing screen — in the collapsed rail this is the only
                      place the label is visible at all. */}
                  <button
                    type="button"
                    className="dpr-rail__flyout-head"
                    onClick={() => {
                      setFlyId(null);
                      item.onSelect?.();
                    }}
                  >
                    {item.label}
                    {badgeText ? <span className="dpr-rail__flyout-badge">{badgeText}</span> : null}
                  </button>
                  <ul className="dpr-rail__flyout-list">
                    {item.children!.map((sub) => {
                      const SubEl = sub.href ? 'a' : 'button';
                      return (
                        <li key={sub.id}>
                          <SubEl
                            className="dpr-rail__flyout-item"
                            href={sub.href}
                            type={sub.href ? undefined : 'button'}
                            onClick={() => {
                              setFlyId(null);
                              sub.onSelect?.();
                            }}
                          >
                            {sub.label}
                          </SubEl>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
